import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { Vector2 } from "../../common/vector2.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { ImgID } from "../../image_loader/img_id.js";
import { DialogManager } from "./dialog_manager.js";
import { Game } from "../../game.js";

export abstract class AbstractDialog extends UIElement
{
  private close_on_background_click = true;
  private appear_animation_started = false;
  private is_closing = false;
  private close_animation_started_at = 0;
  private close_duration_ms = 0;
  private surface_animation: Animation | null = null;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, close_on_background_click: boolean = true )
  {
    super( ctx, x, y, width, height );

    this.close_on_background_click = close_on_background_click;
  }

  getCloseOnBackgroundClick(): boolean
  {
    return this.close_on_background_click;
  }

  public close( reason: string = 'programmatic' ): void
  {
    DialogManager.closeDialogInstance( this, reason );
  }

  public isPoolable(): boolean
  {
    return false;
  }

  public poolKey(): string
  {
    return this.name();
  }

  protected rebuildChildren( build: () => void ): void
  {
    for ( const child of this.getChildren() )
      child.cleanup();
    this.removeAllChildren();
    build();
    this.invalidateSubtree();
  }

  public onReuse( _params: unknown ): void
  {
  }

  public onPooled(): void
  {
  }

  private reused = false;

  public wasReused(): boolean
  {
    return this.reused;
  }

  public resetForReuse(): void
  {
    this.reused = true;
    this.is_closing = false;
    this.appear_animation_started = false;
    this.close_animation_started_at = 0;
    this.close_duration_ms = 0;
    this.cancelSurfaceAnimation();
    this.removeCloseButtonPop();
    if ( this.isDomMode() )
    {
      const node = this.getDomNode();
      node.style.display = "";
      node.style.opacity = "";
      node.style.scale = "";
      this.setDomHidden( false );
    }
    this.invalidateSubtree();
  }

  public hideIntoPool(): void
  {
    this.cancelSurfaceAnimation();
    this.removeCloseButtonPop();
    if ( this.isDomMode() )
    {
      this.setDomHidden( true );
      this.getDomNode().style.display = "none";
    }
  }

  public setSurfaceAnimation( anim: Animation | null ): void
  {
    this.cancelSurfaceAnimation();
    this.surface_animation = anim;
  }

  public cancelSurfaceAnimation(): void
  {
    if ( !this.surface_animation )
      return;
    this.surface_animation.cancel();
    this.surface_animation = null;
  }

  update( now:number ): void
  {
    this.updateBasic( now );
  }

  public onDialogShown(): void
  {
  }

  public onDialogOpened(): void
  {
    if ( this.appear_animation_started )
      return;

    this.appear_animation_started = true;
    this.playAppearAnimation();
  }

  public startCloseAnimation(
    to_scale: number = 0.7,
    duration_ms: number = 144
  ): void
  {
    if ( this.is_closing )
      return;

    this.is_closing = true;
    this.close_duration_ms = duration_ms;
    this.close_animation_started_at = performance.now();
    this.removeCloseButtonPop();

    // Canvas taps preventDefault, so a focused HTML input keeps the keyboard up
    // through the close; its overlay would also track the CSS-scaled canvas and
    // slide down. Drop focus now so it hides with the dialog.
    this.releaseFocusRecursive();
    this.onCloseAnimationStarted( to_scale, duration_ms );

    if ( DialogManager.useDomAnimations( this ) )
    {
      DialogManager.animateCloseDomNode( this, to_scale, duration_ms );
      return;
    }

    if ( DialogManager.useCssAnimations( this ) )
    {
      DialogManager.animateCloseCss( this, to_scale, duration_ms );
      return;
    }

    const start_scale = this.getScaleImportant().clone();

    this.fadeOut( duration_ms, this.close_animation_started_at, Easing.EaseOutQuad );

    this.addTransform( new TransformScale(
      this,
      true,
      start_scale,
      new Vector2( to_scale, to_scale ),
      this.close_animation_started_at,
      duration_ms,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  // Override to animate DOM layers the dialog owns outside the dialog canvas
  // (see DialogManager.animateCloseDom); the canvas animation does not reach them.
  protected onCloseAnimationStarted( _to_scale: number, _duration_ms: number ): void { }

  public isClosing(): boolean
  {
    return this.is_closing;
  }

  public isCloseAnimationFinished( now: number ): boolean
  {
    if ( !this.is_closing )
      return false;

    return now >= this.close_animation_started_at + this.close_duration_ms;
  }

  protected playAppearAnimation(
    from_scale: number = 0.7,
    overshoot_scale: number = 1.1,
    final_scale: number = 1.0,
    scale_up_duration: number = 136,
    settle_duration: number = 96
  ): void
  {
    this.appear_animation_started = true;
    const now = performance.now();

    if ( DialogManager.useDomAnimations( this ) )
    {
      DialogManager.animateAppearDom( this, {
        from_scale,
        overshoot_scale,
        final_scale,
        scale_up_ms: scale_up_duration,
        settle_ms: settle_duration,
      } );
      return;
    }

    if ( DialogManager.useCssAnimations( this ) )
    {
      DialogManager.animateAppearCss( this, from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );
      return;
    }

    this.setScale( new Vector2( from_scale, from_scale ) );
    this.setOpacity( 0 );
    this.fadeIn( scale_up_duration, now, Easing.EaseOutQuad );

    this.addTransform( new TransformScale(
      this,
      true,
      new Vector2( from_scale, from_scale ),
      new Vector2( overshoot_scale, overshoot_scale ),
      now,
      scale_up_duration,
      { easing: Easing.EaseOutQuad }
    ) );

    this.addTransform( new TransformScale(
      this,
      true,
      new Vector2( overshoot_scale, overshoot_scale ),
      new Vector2( final_scale, final_scale ),
      now + scale_up_duration,
      settle_duration,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  public playReturnToTopAnimation(
    from_scale: number = 0.96,
    final_scale: number = 1.0,
    duration_ms: number = 112
  ): void
  {
    const now = performance.now();

    if ( DialogManager.useDomAnimations( this ) )
    {
      DialogManager.animateReturnToTopDom( this, from_scale, final_scale, duration_ms );
      return;
    }

    if ( DialogManager.useCssAnimations( this ) )
    {
      // The canvas still holds the previous (closed) dialog's pixels; without
      // canvas-side transforms nothing re-renders this dialog, so mark the
      // whole subtree dirty for the same-frame repaint.
      this.invalidateSubtree();
      DialogManager.animateReturnToTopCss( this, from_scale, final_scale, duration_ms );
      return;
    }

    this.setScale( new Vector2( from_scale, from_scale ) );
    this.setOpacity( 0 );
    this.fadeIn( duration_ms, now, Easing.EaseOutQuad );

    this.addTransform( new TransformScale(
      this,
      true,
      new Vector2( from_scale, from_scale ),
      new Vector2( final_scale, final_scale ),
      now,
      duration_ms,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  private close_btn_pop_div: HTMLDivElement | null = null;
  private pop_anim: Animation | null = null;
  private pending_pop: { btn: UIImage; delay_ms: number; up_ms: number; settle_ms: number } | null = null;
  private static readonly POP_EASE = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  protected popCloseButtonAfterAppear(
    btn: UIImage,
    appear_delay_ms: number,
    up_ms: number = 176,
    settle_ms: number = 112
  ): void
  {
    if ( !DialogManager.useCssAnimations( this ) )
    {
      const btn_start = performance.now() + appear_delay_ms;
      btn.addTransform( new TransformScale(
        btn, true,
        new Vector2( 0, 0 ), new Vector2( 1.25, 1.25 ),
        btn_start, up_ms,
        { easing: Easing.EaseOutQuad }
      ) );
      btn.addTransform( new TransformScale(
        btn, true,
        new Vector2( 1.25, 1.25 ), new Vector2( 1.0, 1.0 ),
        btn_start + up_ms, settle_ms,
        { easing: Easing.EaseOutQuad }
      ) );
      return;
    }

    // setScale(0) alone only sets the target: scale_for_render stays 1 until a
    // transform update runs, so the dialog's first render would draw the button
    // full-size next to the DOM pop copy. Hide it for real.
    btn.setScaleImmediate( new Vector2( 0, 0 ) );
    this.pending_pop = { btn, delay_ms: appear_delay_ms, up_ms, settle_ms };
  }

  // DOM backend: the surface appear animation has just started on the dialog's node. Override
  // to run the same keyframes on DOM layers the dialog keeps outside it (DialogManager.animateAppearDomLayer).
  public onDomAppearStarted( _keyframes: Keyframe[], _duration_ms: number ): void { }

  public onCssAppearStarted(): void
  {
    const pop = this.pending_pop;
    this.pending_pop = null;
    if ( !pop )
      return;

    const bmp = pop.btn.getImage();
    if ( !bmp )
    {
      pop.btn.setScale( new Vector2( 1, 1 ) );
      return;
    }

    const abs = pop.btn.getAbsolutePosition();
    const w = pop.btn.getRect().getW();
    const h = pop.btn.getRect().getH();
    const dpr = Game.getDpr();

    const div = document.createElement( "div" );
    div.style.cssText = `position:absolute;left:${Game.getGameX() + abs.x}px;top:${abs.y}px;width:${w}px;height:${h}px;z-index:121;pointer-events:none;`;
    const cv = document.createElement( "canvas" );
    cv.width = Math.max( 1, Math.ceil( w * dpr ) );
    cv.height = Math.max( 1, Math.ceil( h * dpr ) );
    cv.style.cssText = `width:${w}px;height:${h}px;`;
    const cctx = cv.getContext( "2d" );
    if ( !cctx )
    {
      pop.btn.setScale( new Vector2( 1, 1 ) );
      return;
    }
    cctx.scale( dpr, dpr );
    cctx.drawImage( bmp, 0, 0, w, h );
    cv.style.transform = "scale(0)";
    div.appendChild( cv );
    document.body.appendChild( div );
    this.close_btn_pop_div = div;

    const total = pop.up_ms + pop.settle_ms;
    this.pop_timer = window.setTimeout( () =>
    {
      this.pop_timer = 0;
      if ( !this.close_btn_pop_div )
        return;

      const anim = cv.animate( [
        { transform: "scale(0)", offset: 0, easing: AbstractDialog.POP_EASE },
        { transform: "scale(1.25)", offset: pop.up_ms / total, easing: AbstractDialog.POP_EASE },
        { transform: "scale(1)", offset: 1 },
      ], { duration: total, fill: "both" } );
      this.pop_anim = anim;

      anim.onfinish = () =>
      {
        this.removeCloseButtonPop();
        pop.btn.setScaleImmediate( new Vector2( 1, 1 ) );

        // Blit the button once instead of invalidating the whole dialog: a full
        // re-render costs a 50-100ms frame on weak GPUs right as the pop lands.
        if ( DialogManager.getTopDialog() === this && !this.isClosing() )
        {
          const ctx = DialogManager.ctx;
          ctx.save();
          ctx.drawImage( bmp, abs.x, abs.y, w, h );
          ctx.restore();
        }
      };
    }, pop.delay_ms );
  }

  private pop_timer = 0;

  // Also called by DialogManager when a dialog is dropped without a close
  // animation (closeAllDialogs): the pop copy lives in document.body above
  // every screen, so it would otherwise outlive the dialog.
  public removeCloseButtonPop(): void
  {
    this.pending_pop = null;
    if ( this.pop_timer )
    {
      clearTimeout( this.pop_timer );
      this.pop_timer = 0;
    }
    if ( this.pop_anim )
    {
      this.pop_anim.cancel();
      this.pop_anim = null;
    }
    if ( this.close_btn_pop_div?.parentNode )
      this.close_btn_pop_div.parentNode.removeChild( this.close_btn_pop_div );
    this.close_btn_pop_div = null;
  }

  public getCenterGlobal(): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2(
      pixel_pos.x + this.rect.getX() + this.rect.getW() / 2,
      pixel_pos.y + this.rect.getY() + this.rect.getH() / 2
    );
  }

  abstract render(): void;
  abstract name(): string;

  public getBackgroundImageId(): ImgID | null { return null; }

  public onDialogWillClose(): void { /* no-op by default, override to react to dialog close */ }
  public onDialogCloseRequested( _reason: string ): void { /* no-op by default, override to record close reason */ }

  containsPointGlobal( p: Vector2 ): boolean
  {
    const pixel_pos = this.pixelPosForRender();
    const scale = this.getScaleImportant();

    const cx = pixel_pos.x + this.rect.getX() + this.rect.getW() / 2;
    const cy = pixel_pos.y + this.rect.getY() + this.rect.getH() / 2;
    const half_w = ( this.rect.getW() * scale.x ) / 2;
    const half_h = ( this.rect.getH() * scale.y ) / 2;

    if ( p.x > cx - half_w && p.x < cx + half_w
      && p.y > cy - half_h && p.y < cy + half_h )
      return true;

    for ( const child of this.getChildren() )
    {
      const r = child.getAbsoluteRect();
      if ( p.x >= r.x && p.x <= r.x + r.width
        && p.y >= r.y && p.y <= r.y + r.height )
        return true;
    }

    return false;
  }

  protected override containsPointInner( local_p: Vector2 ): boolean
  {
    if ( super.containsPointInner( local_p ) )
      return true;

    for ( const child of this.getChildren() )
    {
      const cp = child.pixelPosForRender();
      const r  = child.getRect();
      if ( local_p.x >= cp.x && local_p.x <= cp.x + r.getW()
        && local_p.y >= cp.y && local_p.y <= cp.y + r.getH() )
        return true;
    }

    return false;
  }

  onBackgroundClick(): void
  {
    if ( this.getCloseOnBackgroundClick() )
      this.close( 'background_tap' );
  }
}
